import PieChartWidget from './pieChartWidget'
import { checkIfAllEmpty } from './utils'
import {
  ACTION_FETCH_PIA_ASSESSMENTS_BASED_ON_RISK_REVIEWS,
  PiaSummary,
  RiskAssessmentsCounts,
  fetchPiaAssessmentsBasedOnRiskReviews,
  fetchPiaSummary
} from '../privacyOpsOverviewSlice'
import { PiaRiskOption, fetchPiaRiskOptions } from '../../pia/piaSlice'
import { RootState } from '../../../rootReducer'
import { getShowLoader } from '../../../reducers/requestReducer'
import { getUpdatedUrl } from '../../../utils/urlUtil'
import {
  FILTER_REVIEW_SCHEDULE,
  FILTER_REVIEW_STATUS,
  PAGE_VIEW_KEY,
  PAGE_VIEW_VALUES,
  PiaProcessFilter,
  URL_DEFINED_PROCESSES,
  URL_PIA
} from '../../../constants'
import { useIntl } from 'react-intl'
import React, { useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { Typography } from '@lightbeamai/design-system'
import { Link } from 'react-router-dom'

interface IProps {
  piaRiskAssessmentsCounts?: RiskAssessmentsCounts
  piaSummary?: PiaSummary
  timePeriod?: string
  riskOptions?: PiaRiskOption[]
  fetchPiaAssessmentsBasedOnRiskReviews: (timePeriod: string) => void
  fetchPiaSummary: (timePeriod: string) => void
  showLoader: boolean
  fetchPiaRiskOptions: () => void
}

const PiaWidget = ({
  piaRiskAssessmentsCounts,
  piaSummary,
  timePeriod = '',
  fetchPiaAssessmentsBasedOnRiskReviews,
  fetchPiaSummary,
  showLoader,
  riskOptions = [],
  fetchPiaRiskOptions
}: IProps) => {
  const intl = useIntl()

  useEffect(() => {
    fetchPiaAssessmentsBasedOnRiskReviews(timePeriod)
  }, [fetchPiaAssessmentsBasedOnRiskReviews, timePeriod])

  useEffect(() => {
    fetchPiaSummary(timePeriod)
  }, [fetchPiaSummary, timePeriod])

  useEffect(() => {
    fetchPiaRiskOptions()
  }, [fetchPiaRiskOptions])

  const formattedData = useMemo(
    () =>
      piaRiskAssessmentsCounts?.map((item) => ({
        key: item.riskType,
        value: item.count,
        color: item.color,
        link: getUpdatedUrl({
          pathName: `${URL_PIA}${URL_DEFINED_PROCESSES}`,
          pageName: URL_DEFINED_PROCESSES,
          pageQueryParams: {
            [PAGE_VIEW_KEY]: PAGE_VIEW_VALUES.list,
            [FILTER_REVIEW_SCHEDULE]: item.id,
            [FILTER_REVIEW_STATUS]: PiaProcessFilter.reviewed
          }
        })
      })) || [],
    [piaRiskAssessmentsCounts]
  )
  const PieChartLabel = () => {
    return (
      <div className="title">{intl.formatMessage({ id: 'po.dashboard.pia.widget.helpText' })}</div>
    )
  }
  return (
    <PieChartWidget
      title={intl.formatMessage({ id: 'po.dashboard.pia.widget.title' })}
      help={<PieChartLabel />}
      data={checkIfAllEmpty(formattedData)}
      legendPosition="right"
      showLoader={showLoader}
      showSummary={true}
      viewAllBtnText={intl.formatMessage({
        id: 'po.dashboard.riskBasedOnGroups.widget.viewAllBtn'
      })}
      viewAllLink={getUpdatedUrl({
        pathName: `${URL_PIA}${URL_DEFINED_PROCESSES}`,
        pageName: URL_DEFINED_PROCESSES,
        pageQueryParams: {
          [PAGE_VIEW_KEY]: PAGE_VIEW_VALUES.list,
          [FILTER_REVIEW_STATUS]: [PiaProcessFilter.reviewed].join(','),
          [FILTER_REVIEW_SCHEDULE]: riskOptions?.map((item) => item.id).join()
        }
      })}
      Summary={
        <div className="xs-mr-32 xs-mt-16">
          <Link
            to={getUpdatedUrl({
              pathName: `${URL_PIA}${URL_DEFINED_PROCESSES}`,
              pageName: URL_DEFINED_PROCESSES,
              pageQueryParams: {
                [PAGE_VIEW_KEY]: PAGE_VIEW_VALUES.list
              }
            })}
            className="lb-summary-total-counts xs-mb-8 xs-px-8 xs-py-4"
          >
            <Typography variant={'basePlus6'} type={'strong'}>
              {piaSummary?.assessmentsCount || 0}
            </Typography>
            <Typography variant={'baseMinus2'} type={'medium'}>
              {intl.formatMessage({ id: 'pia.summary.totalAssessments' })}
            </Typography>
          </Link>
          <Link
            to={getUpdatedUrl({
              pathName: `${URL_PIA}${URL_DEFINED_PROCESSES}`,
              pageName: URL_DEFINED_PROCESSES,
              pageQueryParams: {
                [PAGE_VIEW_KEY]: PAGE_VIEW_VALUES.list,
                [FILTER_REVIEW_STATUS]: [
                  PiaProcessFilter.reviewPending,
                  PiaProcessFilter.inProgress,
                  PiaProcessFilter.notSubmitted
                ].join(',')
              }
            })}
            className="lb-summary-total-counts xs-mb-8 xs-px-8 xs-py-4"
          >
            <Typography variant={'basePlus6'} type={'strong'}>
              {piaSummary?.pendingReviewAssessmentsCount || 0}
            </Typography>
            <Typography variant={'baseMinus2'} type={'medium'}>
              {intl.formatMessage({ id: 'pia.summary.needReview' })}
            </Typography>
          </Link>
        </div>
      }
    />
  )
}

const mapStateToProps = (state: RootState) => ({
  piaRiskAssessmentsCounts: state.privacyOpsOverview.piaRiskAssessmentsCounts,
  piaSummary: state.privacyOpsOverview.piaSummary,
  riskOptions: state.pia.riskOptions,
  timePeriod: state.customDashboards.filterProps.timePeriod,
  showLoader: getShowLoader(ACTION_FETCH_PIA_ASSESSMENTS_BASED_ON_RISK_REVIEWS)
})

const mapDispatchToProps = {
  fetchPiaAssessmentsBasedOnRiskReviews,
  fetchPiaSummary,
  fetchPiaRiskOptions
}

export default connect(mapStateToProps, mapDispatchToProps)(PiaWidget)
